/**
 * @fileoverview A escolha do que fica num plano menor (31/10/2026, etapa 9 dos
 * planos), no mesmo modal largo da contratação: à esquerda, o que a Empresa tem
 * (contas, números, quadros, pessoas e quem mantém o Chat Interno), marcado o
 * que fica; à direita, cada limite do plano novo com o que fica, e o que sai.
 * O número e o quadro saem junto com a conta; quem fica precisa de uma conta
 * que fica; quem escolhe não sai; um administrador fica. Com a redução ou o
 * cancelamento agendado, a escolha fica gravada até a renovação (e muda até
 * lá); no acesso restrito, salvar arquiva na hora e devolve o acesso. O
 * servidor confere tudo de novo.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, IconButton } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { X } from "lucide-react";

import { billingErrorText } from "../PlanControls";
import api from "../../../services/api";
import { i18n } from "../../../translate/i18n";
import { formatSaoPauloDate, formatUtcDate } from "../../../utils/formatLocaleDate";
import usePlansStyles from "../PlansDialog/styles";
import useStyles from "./styles";

const t = (key, options) => i18n.t(`adequacyDialog.${key}`, options);
const RESOURCES = ["users", "internalChatUsers", "accounts", "whatsapps", "crmBoards"];

const without = (set, id) => {
  const next = new Set(set);
  next.delete(id);
  return next;
};
const toggled = (set, id) => (set.has(id) ? without(set, id) : new Set(set).add(id));

/** O que fica, a partir da escolha gravada (o que sai) ou, sem ela, tudo. */
const initialState = (adequacy) => {
  const choice = adequacy.choice;
  const leaving = (list) => new Set(choice ? list : []);
  const allWhatsapps = adequacy.accounts.flatMap((account) => account.whatsapps.map((w) => w.id));
  const allBoards = adequacy.accounts.flatMap((account) => account.crmBoards.map((b) => b.id));
  return {
    accounts: new Set(adequacy.accounts.map((a) => a.id).filter((id) => !leaving(choice?.accounts || []).has(id))),
    whatsapps: new Set(allWhatsapps.filter((id) => !leaving(choice?.whatsapps || []).has(id))),
    crmBoards: new Set(allBoards.filter((id) => !leaving(choice?.crmBoards || []).has(id))),
    users: new Set(adequacy.members.map((m) => m.id).filter((id) => !leaving(choice?.users || []).has(id))),
    internalChat: new Set(adequacy.members.filter((m) => m.internalChat).map((m) => m.id).filter((id) => !leaving(choice?.internalChatOff || []).has(id))),
  };
};

const Check = ({ classes, checked, disabled, onChange, label, hint, tag }) => (
  <label className={`${classes.item} ${disabled ? classes.itemOff : ""}`}>
    <input type="checkbox" className={classes.box} checked={checked} disabled={disabled} onChange={onChange} />
    <span className={classes.itemText}>
      <span className={classes.itemName}>
        {label}
        {tag && <span className={classes.tag}>{tag}</span>}
      </span>
      {hint && <span className={classes.itemHint}>{hint}</span>}
    </span>
  </label>
);

const Section = ({ classes, title, used, limit, children, id }) => (
  <section className={classes.section} aria-labelledby={id}>
    <div className={classes.sectionHead}>
      <h3 className={classes.sectionTitle} id={id}>
        {title}
      </h3>
      <span className={`${classes.count} ${limit !== null && used > limit ? classes.countOver : ""}`}>
        {limit === null ? t("countFree", { used }) : t("count", { used, limit })}
      </span>
    </div>
    {children}
  </section>
);

const AdequacyDialog = ({ open, adequacy, planName, onClose, onSaved }) => {
  const plans = usePlansStyles();
  const classes = useStyles();
  const [state, setState] = useState(() => initialState(adequacy));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const errorRef = useRef(null);
  const restricted = adequacy.mode === "restricted";
  // No prazo depois do fim de uma exceção de limite (etapa 11), a escolha também vale na hora.
  const due = adequacy.mode === "due";
  const now = restricted || due;

  useEffect(() => {
    if (open) {
      setState(initialState(adequacy));
      setError(null);
    }
  }, [open, adequacy]);
  useEffect(() => {
    if (error && errorRef.current) errorRef.current.focus();
  }, [error]);

  const derived = useMemo(() => {
    const accountStays = (id) => state.accounts.has(id);
    const whatsappsStaying = adequacy.accounts.filter((a) => accountStays(a.id)).flatMap((a) => a.whatsapps.filter((w) => state.whatsapps.has(w.id)));
    const boardsStaying = adequacy.accounts.filter((a) => accountStays(a.id)).flatMap((a) => a.crmBoards.filter((b) => state.crmBoards.has(b.id)));
    // A conta sem quadro ganha o "Principal" sozinha: ocupa a vaga.
    const reserved = adequacy.accounts.filter((a) => accountStays(a.id) && !a.crmBoards.some((b) => state.crmBoards.has(b.id))).length;
    const withAccount = (member) => member.accountIds.some(accountStays);
    const membersStaying = adequacy.members.filter((m) => state.users.has(m.id) && withAccount(m));
    const internalStaying = membersStaying.filter((m) => m.internalChat && (m.internalOnly || m.sharedPerson || state.internalChat.has(m.id)));
    const counts = {
      users: membersStaying.length,
      internalChatUsers: internalStaying.length,
      accounts: adequacy.accounts.filter((a) => accountStays(a.id)).length,
      whatsapps: whatsappsStaying.length,
      crmBoards: boardsStaying.length + reserved,
    };
    const over = RESOURCES.filter((r) => adequacy.limits[r] !== null && counts[r] > adequacy.limits[r]);
    const hasAdmin = membersStaying.some((m) => m.isAdmin);
    // O que sai, para o servidor: só o escolhido (o número e o quadro de uma conta que sai vão junto com ela).
    const removal = {
      accounts: adequacy.accounts.filter((a) => !accountStays(a.id)).map((a) => a.id),
      whatsapps: adequacy.accounts.filter((a) => accountStays(a.id)).flatMap((a) => a.whatsapps.filter((w) => !state.whatsapps.has(w.id)).map((w) => w.id)),
      crmBoards: adequacy.accounts.filter((a) => accountStays(a.id)).flatMap((a) => a.crmBoards.filter((b) => !state.crmBoards.has(b.id)).map((b) => b.id)),
      users: adequacy.members.filter((m) => !membersStaying.includes(m)).map((m) => m.id),
      internalChatOff: membersStaying.filter((m) => m.internalChat && !m.internalOnly && !m.sharedPerson && !state.internalChat.has(m.id)).map((m) => m.id),
    };
    const leavingWhatsapps = adequacy.accounts.reduce((sum, a) => sum + a.whatsapps.length, 0) - whatsappsStaying.length;
    const leavingBoards = adequacy.accounts.reduce((sum, a) => sum + a.crmBoards.length, 0) - boardsStaying.length;
    return { counts, over, hasAdmin, removal, withAccount, accountStays, leaving: { accounts: removal.accounts.length, whatsapps: leavingWhatsapps, crmBoards: leavingBoards, users: removal.users.length, internalChatOff: removal.internalChatOff.length } };
  }, [state, adequacy]);

  const valid = derived.over.length === 0 && derived.counts.accounts > 0 && derived.hasAdmin;
  const flip = (key, id) => setState((current) => ({ ...current, [key]: toggled(current[key], id) }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // O modo em que a tela abriu: se a situação mudou (o prazo abriu com o diálogo da redução aberto), o servidor recusa.
      const { data } = await api.put("/billing/adequacy", { ...derived.removal, mode: adequacy.mode });
      // Salvo, o modal fecha: nada mais muda aqui.
      onSaved(data.adequacy);
    } catch (err) {
      setError(err?.response?.data?.error || "network");
      setSaving(false);
    }
  };

  const leavingText = () => {
    const parts = ["accounts", "whatsapps", "crmBoards", "users"].filter((k) => derived.leaving[k] > 0).map((k) => t(`leaving.${k}`, { count: derived.leaving[k] }));
    if (derived.leaving.internalChatOff > 0) parts.push(t("leaving.internalChatOff", { count: derived.leaving.internalChatOff }));
    return parts.length ? parts.join(", ") : t("leaving.nothing");
  };

  const internalMembers = adequacy.members.filter((m) => m.internalChat && state.users.has(m.id) && derived.withAccount(m));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth={false} classes={{ paper: plans.dialogPaper }} aria-labelledby="adequacy-dialog-title">
      <div className={plans.dialogHead}>
        <div>
          <h2 className={plans.dialogTitle} id="adequacy-dialog-title">
            {t("title", { plan: planName })}
          </h2>
          <p className={plans.dialogText}>
            {restricted ? t("subtitleRestricted") : due ? t("subtitleDue", { date: formatSaoPauloDate(adequacy.deadline) }) : t("subtitle", { date: formatUtcDate(adequacy.deadline) })}
          </p>
        </div>
        <IconButton size="small" className={plans.dialogClose} onClick={onClose} disabled={saving} aria-label={t("close")}>
          <X size={18} />
        </IconButton>
      </div>
      <div className={plans.body}>
        <div className={`${plans.choices} ${classes.lists}`}>
          <Section classes={classes} id="adequacy-accounts" title={t("accounts")} used={derived.counts.accounts} limit={adequacy.limits.accounts}>
            {adequacy.accounts.map((account) => {
              const goes = account.whatsapps.length + account.crmBoards.length;
              return (
                <Check
                  key={account.id}
                  classes={classes}
                  checked={state.accounts.has(account.id)}
                  onChange={() => flip("accounts", account.id)}
                  label={account.name}
                  hint={!state.accounts.has(account.id) && goes > 0 ? t("accountGoesWith", { whatsapps: account.whatsapps.length, boards: account.crmBoards.length }) : null}
                />
              );
            })}
          </Section>

          <Section classes={classes} id="adequacy-whatsapps" title={t("whatsapps")} used={derived.counts.whatsapps} limit={adequacy.limits.whatsapps}>
            {adequacy.accounts.every((a) => a.whatsapps.length === 0) && <p className={classes.empty}>{t("noWhatsapps")}</p>}
            {adequacy.accounts
              .filter((a) => a.whatsapps.length)
              .map((account) => (
                <div key={account.id} className={classes.group}>
                  {adequacy.accounts.length > 1 && <span className={classes.groupName}>{account.name}</span>}
                  {account.whatsapps.map((w) => (
                    <Check
                      key={w.id}
                      classes={classes}
                      checked={derived.accountStays(account.id) && state.whatsapps.has(w.id)}
                      disabled={!derived.accountStays(account.id)}
                      onChange={() => flip("whatsapps", w.id)}
                      label={w.name}
                      hint={!derived.accountStays(account.id) ? t("goesWithAccount") : w.number || null}
                    />
                  ))}
                </div>
              ))}
          </Section>

          <Section classes={classes} id="adequacy-boards" title={t("crmBoards")} used={derived.counts.crmBoards} limit={adequacy.limits.crmBoards}>
            {adequacy.accounts.filter((account) => account.crmBoards.length || derived.accountStays(account.id)).map((account) => (
              <div key={account.id} className={classes.group}>
                {adequacy.accounts.length > 1 && <span className={classes.groupName}>{account.name}</span>}
                {account.crmBoards.length === 0 && <p className={classes.empty}>{t("noBoards")}</p>}
                {account.crmBoards.map((b) => (
                  <Check
                    key={b.id}
                    classes={classes}
                    checked={derived.accountStays(account.id) && state.crmBoards.has(b.id)}
                    disabled={!derived.accountStays(account.id)}
                    onChange={() => flip("crmBoards", b.id)}
                    label={b.name}
                    tag={b.isDefault ? t("defaultBoard") : null}
                    hint={!derived.accountStays(account.id) ? t("goesWithAccount") : null}
                  />
                ))}
              </div>
            ))}
            <p className={classes.note}>{t("boardsNote")}</p>
          </Section>

          <Section classes={classes} id="adequacy-users" title={t("users")} used={derived.counts.users} limit={adequacy.limits.users}>
            {adequacy.members.map((member) => {
              const noAccount = !derived.withAccount(member);
              return (
                <Check
                  key={member.id}
                  classes={classes}
                  checked={!noAccount && state.users.has(member.id)}
                  disabled={member.isSelf || noAccount}
                  onChange={() => flip("users", member.id)}
                  label={member.name}
                  tag={member.isSelf ? t("you") : member.isAdmin ? t("admin") : null}
                  hint={noAccount ? t("userWithoutAccount") : member.email}
                />
              );
            })}
            {!derived.hasAdmin && <p className={classes.warn}>{t("needAdmin")}</p>}
          </Section>

          {internalMembers.length > 0 && (
            <Section classes={classes} id="adequacy-internal" title={t("internalChat")} used={derived.counts.internalChatUsers} limit={adequacy.limits.internalChatUsers}>
              {internalMembers.map((member) => (
                <Check
                  key={member.id}
                  classes={classes}
                  checked={member.internalOnly || member.sharedPerson || state.internalChat.has(member.id)}
                  disabled={member.internalOnly || member.sharedPerson}
                  onChange={() => flip("internalChat", member.id)}
                  label={member.name}
                  hint={member.internalOnly ? t("internalOnly") : member.sharedPerson ? t("sharedPerson") : null}
                />
              ))}
            </Section>
          )}
        </div>

        <aside className={plans.summary} aria-labelledby="adequacy-summary">
          <h3 className={plans.summaryTitle} id="adequacy-summary">
            {t("summaryTitle", { plan: planName })}
          </h3>
          <ul className={classes.limits}>
            {RESOURCES.map((resource) => {
              const limit = adequacy.limits[resource];
              const over = limit !== null && derived.counts[resource] > limit;
              return (
                <li key={resource} className={classes.limitRow}>
                  <span>{t(`limits.${resource}`)}</span>
                  <span className={over ? classes.countOver : classes.limitValue}>
                    {limit === null ? t("countFree", { used: derived.counts[resource] }) : t("count", { used: derived.counts[resource], limit })}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className={classes.leaving}>
            <span className={classes.leavingTitle}>{now ? t("leavingNow") : t("leavingTitle")}</span>
            <span className={classes.leavingText}>{leavingText()}</span>
          </div>
          <p className={`${plans.methodNote} ${classes.keepNote}`}>{t("keepNote")}</p>
          {error && (
            <p className={plans.formError} role="alert" tabIndex={-1} ref={errorRef}>
              {billingErrorText(error)}
            </p>
          )}
          <span className={plans.spacer} />
          {!valid && <p className={classes.warn}>{derived.over.length ? t("overHint") : t("needAdmin")}</p>}
          <button type="button" className={plans.primary} onClick={save} disabled={saving || !valid}>
            {saving && <CircularProgress size={16} color="inherit" />}
            {restricted ? t("saveRestricted") : due ? t("saveDue") : t("save")}
          </button>
        </aside>
      </div>
    </Dialog>
  );
};

export default AdequacyDialog;
